import type { FeatureCollection } from "geojson";
import MapLibreGL, {
  FillLayerSpecification,
  Map as GlMap,
  LineLayerSpecification,
  SymbolLayerSpecification,
  CircleLayerSpecification,
} from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { Protocol } from "pmtiles";
import { useCallback, useContext } from "react";
import Map, {
  AttributionControl,
  GeolocateControl,
  Layer,
  MapRef,
  NavigationControl,
  ScaleControl,
  Source,
} from "react-map-gl/maplibre";
import { useNavigate } from "react-router-dom";
import { CaMapContext, useLayers } from "../state";
import "./CaMap.scss";
import { fake, ImageOverlayControl, transparentPng } from "./controls/ImageOverlayControl";
import { LayersControl } from "./controls/LayersControl";
import { ProjectControl } from "./controls/ProjectControl";
import { SearchControl } from "./controls/SearchControl";
import { BuildingsControl, SelectControl } from "./controls/SelectControl";
import { createFlagImage } from "./icons/flag";
import { defaultViewState } from "./viewstate";

const layer = "default";
const beforeId = "waterway_line_label"; //"label-address-housenumber"
const parcelLayer = (
  <Layer
    id="parcel"
    source-layer={layer}
    beforeId={beforeId}
    type="fill"
    paint={{
      "fill-outline-color": "rgb(7, 119, 53)",
      "fill-color": [
        "case",
        ["boolean", ["feature-state", "hover"], false],
        "rgba(219,247,227, 0.6)",
        "rgba(0,0,0,0)",
      ],
    }}
  />
);

const ParcelLine = ({ ...props }) => {
  const aerial = useLayers((s) => s.backgroundId !== "default");
  return (
    <Layer
      id="parcelLine"
      beforeId={beforeId}
      type="line"
      paint={{
        "line-color": aerial ? "rgb(239, 201, 2)" : "rgb(7, 119, 53)",
        "line-width": ["interpolate", ["linear"], ["zoom"], 10, 0.2, 18, 1.5],
      }}
      {...props}
    />
  );
};

const labelLayer = (
  <Layer
    type="symbol"
    source-layer="label"
    // source-layer={layer}
    layout={{
      "text-rotation-alignment": "map",
      "text-anchor": "top",
      "text-field": ["step", ["zoom"], ["get", "short_name"], 17.5, ["get", "id"]],
      // "text-field": "{name}",
      "text-font": ["Noto Sans Regular"],
      "text-size": ["interpolate", ["linear"], ["zoom"], 10, 4, 20, 14],
    }}
    minzoom={15}
    paint={{
      "text-color": "#666",
      "text-halo-blur": 1,
      "text-halo-color": "rgba(255,255,255,0.75)",
      "text-halo-width": 1,
    }}
  />
);
// https://api.pdok.nl/lv/bgt/ogc/v0_1/styles/achtergrondvisualisatie?f=mapbox
// https://medium.com/@yixu0215/dynamic-label-placement-with-mapbox-gl-js-turf-polylabel-1f84f1d4bf6b
// https://github.com/Roxyi/dynamic_labels/blob/master/js/map.js

// fill
const selectionFillStyle: Partial<FillLayerSpecification> = {
  paint: {
    "fill-outline-color": "rgb(7, 119, 53)",
    "fill-color": "rgba(250, 221, 139, 0.6)",
  },
};
const selectionStyle: Partial<LineLayerSpecification> = {
  paint: {
    "line-color": "rgb(7, 119, 53)",
    "line-width": 3,
    "line-dasharray": [3, 2],
  },
};
const buildingStyle: Partial<FillLayerSpecification> = {
  paint: {
    "fill-outline-color": "rgb(7, 10, 10)",
    "fill-color": "rgba(30, 30, 30, 0.3)",
  },
};

const circleSearchStyle: Partial<CircleLayerSpecification> = {
  paint: {
    "circle-radius": ["interpolate", ["linear"], ["zoom"], 10, 2, 20, 14],
    "circle-color": "rgba(255, 10, 10, 0.5)",
  },
};


const FLAG_ICON_ID = "search-result-flag";
const flagSearchStyle: Partial<SymbolLayerSpecification> = {
  layout: {
    "icon-image": FLAG_ICON_ID,
    "icon-anchor": "bottom-left",
    "icon-allow-overlap": true,
    "icon-ignore-placement": true,
    "icon-size": ["interpolate", ["linear"], ["zoom"], 10, 0.4, 20, 1.0],
  },
};

const projectFillStyle: Partial<FillLayerSpecification> = {
  paint: {
    "fill-outline-color": [
      "coalesce",
      ["get", "tagOutline"],
      ["case", ["boolean", ["get", "selected"], false], "rgb(202, 138, 4)", "rgb(234, 179, 8)"],
    ],
    "fill-color": [
      "coalesce",
      ["get", "tagFill"],
      [
        "case",
        ["boolean", ["get", "selected"], false],
        "rgba(253, 224, 71, 0.5)",
        "rgba(253, 224, 71, 0.15)",
      ],
    ],
  },
};
const projectStyle: Partial<LineLayerSpecification> = {
  paint: {
    "line-color": [
      "coalesce",
      ["get", "tagOutline"],
      ["case", ["boolean", ["get", "selected"], false], "rgb(146, 64, 14)", "rgb(202, 138, 4)"],
    ],
    "line-width": ["case", ["boolean", ["get", "selected"], false], 3, 2],
    "line-dasharray": [3, 2],
  },
};

const useHover = (map: GlMap) =>
  useCallback(
    (e) => {
      if (!map) return;
      if (hoveredId) {
        map.setFeatureState(
          { source: "parcels", sourceLayer: layer, id: hoveredId },
          { hover: false }
        );
      }
      hoveredId = e.type === "mousemove" && e.features?.[0]?.id;
      if (hoveredId) {
        map.setFeatureState(
          { source: "parcels", sourceLayer: layer, id: hoveredId },
          { hover: true }
        );
      }
      map.getCanvas().style.cursor = hoveredId ? "pointer" : "grab";
    },
    [map]
  );

const parcels = ["parcel"];
let hoveredId = null; // state tracking

const initialViewState = defaultViewState();

const protocol = new Protocol();
MapLibreGL.addProtocol("pmtiles", protocol.tile);

const noData: FeatureCollection = {
  type: "FeatureCollection",
  features: [],
};
export const CAMap = ({ className = undefined, circle=false }) => {
  const [map, setMap] = useContext(CaMapContext);
  const navigate = useNavigate();
  const onHover = useHover(map);

  const onClick = useCallback(
    ({ lngLat }) => navigate(`/location/${lngLat.lng.toFixed(5)},${lngLat.lat.toFixed(5)}`),
    [navigate]
  );

  // @ts-ignore:next-line
  const refCallBack = useCallback<Ref>(
    // @ts-ignore:next-line
    (r: MapRef) => {
      if (!r) return;
      const m = r.getMap();
      setMap((window["map"] = m));
      const flag = createFlagImage();
      // The OpenFreeMap "bright" style references POI icons that aren't in
      // its sprite (bicycle_parking, atm, sports_centre, ...). Provide an
      // empty placeholder so MapLibre stops logging warnings.
      m.on("styleimagemissing", (e) => {
        if (m.hasImage(e.id)) return;
        if (e.id === FLAG_ICON_ID && flag) {
          m.addImage(e.id, flag);
          return;
        }
        m.addImage(e.id, { width: 1, height: 1, data: new Uint8Array(4) });
      });
    },
    [setMap]
  );
  return (
    <div className={className}>
      <Map
        initialViewState={initialViewState}
        mapStyle="https://tiles.openfreemap.org/styles/bright"
        attributionControl={false}
        ref={refCallBack}
        interactiveLayerIds={parcels}
        onClick={onClick}
        onMouseMove={onHover}
        onMouseLeave={onHover}
      >
        <NavigationControl position="top-right" />
        <GeolocateControl position="top-right" />
        <AttributionControl />
        <ScaleControl />
        <Source id="baoverlay" type="image" url={transparentPng} coordinates={fake}>
          <Layer id="baoverlay" type="raster" layout={{ visibility: "none" }} />
          <ImageOverlayControl id="baoverlay" />
        </Source>
        <LayersControl />
        <Source id="searchResults" type="geojson" data={noData}>
          {circle?<Layer id="searchResults" type="circle" {...circleSearchStyle} />:<Layer id="searchResults" type="symbol" {...flagSearchStyle} />}
          <SearchControl />
        </Source>
        <Source
          id="parcels"
          type="vector"
          promoteId="id"
          url="pmtiles://https://ref.cadamap.com/tiles/combined.pmtiles?v=1"
        >
          {parcelLayer}
          <ParcelLine source="parcels" source-layer={layer} />
          {labelLayer}
        </Source>
        <Source id="selection" type="geojson" data={noData}>
          <Layer id="selection" type="line" {...selectionStyle} />
          <Layer id="selectionFill" type="fill" {...selectionFillStyle} />
          <SelectControl id="selection" />
        </Source>
        <Source id="projects" type="geojson" data={noData}>
          <Layer id="projects" type="line" {...projectStyle} />
          <Layer id="projectsFill" type="fill" {...projectFillStyle} />
          <ProjectControl />
        </Source>
        <Source id="buildings" type="geojson" data={noData}>
          <Layer id="buildings" type="fill" {...buildingStyle} />
          <BuildingsControl id="buildings" />
        </Source>
      </Map>
    </div>
  );
};
